/**
 * The Manpower planning assumptions the Capacity Inputs screen states (R3.2, R6.4,
 * Assumption A13).
 *
 * Wired to `GET /api/manpower/planning-assumptions`, which reports the tunables in
 * force: the productive-hours and availability defaults a new Capacity_Period is
 * created with, and the Utilization thresholds a period is classified against.
 *
 * ## Read-only, and why the screen says so
 *
 * There is no write route. Changing one of these recalculates every affected
 * baseline record from the effective date (R3.6) — a Recalculation_Run with its own
 * audit trail and inline-versus-background job semantics — so it is not a PUT that
 * this hook could pair with. The screen therefore presents the assumptions as
 * disclosure rather than as a form, which is the honest rendering of what the API
 * offers today.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch } from '@/shared/api/client';
import { manpowerQueryKeys } from '../manpower-query-keys';

/** The tunables behind every capacity figure on the screen. */
export interface PlanningAssumptions {
  /** Productive hours per available workday, applied to a new Capacity_Period (R3.2). */
  readonly productiveHoursPerWorkday: number;
  /** The availability factor applied to a new Capacity_Period (A13). */
  readonly availabilityFactor: number;
  /** The Utilization percentage at which a period classifies as Warning (R6.4). */
  readonly capacityThresholdPct: number;
  /** The percentage above which a period is Over_Capacity — definitional, always 100. */
  readonly overCapacityThresholdPct: number;
  /** The configured Allocation_Method identifier, e.g. `'even'` (R5.4). */
  readonly allocationMethod: string;
}

const responseSchema = z.object({
  productiveHoursPerWorkday: z.number(),
  availabilityFactor: z.number(),
  capacityThresholdPct: z.number(),
  overCapacityThresholdPct: z.number(),
  allocationMethod: z.string(),
});

/** Fetches the planning assumptions in force (R3.2, R6.4). */
export async function fetchPlanningAssumptions(): Promise<PlanningAssumptions> {
  const body = await apiFetch<unknown>('/api/manpower/planning-assumptions');
  return responseSchema.parse(body);
}

/** React Query hook for {@link fetchPlanningAssumptions}. */
export function usePlanningAssumptions(options?: {
  readonly enabled?: boolean;
}): UseQueryResult<PlanningAssumptions, unknown> {
  return useQuery({
    queryKey: manpowerQueryKeys.planningAssumptions(),
    queryFn: fetchPlanningAssumptions,
    enabled: options?.enabled ?? true,
  });
}
